import React, {
  FC, KeyboardEvent, PointerEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Icon } from '@mdi/react';
import { mdiHeadphones, mdiPause, mdiPlay } from '@mdi/js';

import { useLocale } from 'lib';
import { Range, useHeardRanges } from './useHeardRanges';
import { bucketSeconds, useWaveform } from './useWaveform';
import styles from './style.module.scss';

type RecordingBoxProps = {
  /** A new `url` is the same recording with more audio: the player continues where the operator was. */
  url: string;
  /** The same audio when it is in memory: the waveform reads it from here instead of fetching the url. */
  blob?: Blob;
  /** Identifies the recording, so what was heard is remembered across reloads (sessionStorage). */
  recordingKey: string;
  /** Seconds per joined chunk. Without it the duration comes from the audio itself. */
  chunkDurations?: number[];
  /** Playback never goes past this many seconds (legacy recording: up to the first join). */
  limit?: number | null;
  /** When the newest chunk was stored. */
  lastUploadAt?: number;
  /** No more audio will be added. */
  final?: boolean;
};

const liveWindowMs = 30 * 1000;
// After the recording stopped being live, what was added stays marked as new for this long.
const newAfterLiveMs = 60 * 1000;
// Between two frames playback moves by a frame; more than this is a seek, and a seek is not listening.
const maxPlayedStepSeconds = 1.5;
const durationEaseMs = 1000;
// The audio element that takes over needs a moment to start; start it this far ahead.
const handOverLatencySeconds = 0.08;
const keyboardStepSeconds = 5;
// A recording is heard "from the start" when the first heard part begins this close to 0.
const unheardStartToleranceSeconds = 0.3;
// Less than this left to hear: nothing to jump to.
const minUnheardSeconds = 0.5;
// A heard part that ends this close to the playhead is the one that is being played.
const playingTailSeconds = 0.25;
// The waveform has about one bar per this many pixels, however much audio there is.
const waveBarPixels = 4;

const formatTime = (seconds: number) => {

  const whole = Math.floor(Math.max(0, seconds));

  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;

};

/**
 * One recording with its player. Clock and bar come from the same state, a recording that grows keeps its
 * position (a second audio element takes over, so nothing reloads under the playhead), and the bar shows
 * which parts were really listened to. The box carries `data-guide-done` once all of it was heard, which the
 * "what should I do" guide reads.
 */
export const RecordingBox: FC<RecordingBoxProps> = ({
  url, blob, recordingKey, chunkDurations, limit = null, lastUploadAt, final = true,
}) => {

  const { t } = useLocale();
  const { ranges, mark } = useHeardRanges(`recordingHeard:${recordingKey}`);
  const rangesRef = useRef<Range[]>([]);

  rangesRef.current = ranges;

  const fineBars = useWaveform(url, blob);
  const [barWidth, setBarWidth] = useState(600);
  const audioElements = useRef<(HTMLAudioElement | null)[]>([null, null]);
  const activeIndex = useRef(0);
  const barRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastPosition = useRef(0);
  const swap = useRef(0);
  // Stopped at the end of a recording that is still growing: continue when more audio arrives.
  const resumeOnAppend = useRef(false);
  const previousTotal = useRef(0);
  const shownRef = useRef(0);

  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [metaDuration, setMetaDuration] = useState(0);
  const [freshFrom, setFreshFrom] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [expired, setExpired] = useState(0);

  const active = useCallback(() => audioElements.current[activeIndex.current], []);
  const isActive = (event: SyntheticEvent<HTMLAudioElement>) => event.currentTarget === active();

  const knownDuration = chunkDurations?.reduce((sum, seconds) => sum + seconds, 0);
  const metaCapped = limit && metaDuration ? Math.min(limit, metaDuration) : (limit ?? metaDuration);
  const total = knownDuration ?? metaCapped;
  const end = limit ?? total;
  const [shown, setShown] = useState(total);

  // The length of the bar eases to a new total, so the playhead does not snap when audio is added.
  useEffect(() => {

    const from = shownRef.current;

    if (from === 0 || total <= from) {

      shownRef.current = total;
      setShown(total);
      return undefined;

    }

    const startedAt = performance.now();
    let frame = 0;
    const step = (now: number) => {

      const progress = Math.min(1, (now - startedAt) / durationEaseMs);

      shownRef.current = from + (total - from) * progress;
      setShown(shownRef.current);

      if (progress < 1) frame = requestAnimationFrame(step);

    };

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);

  }, [total]);

  // The newest fragment is shown as new when it is added after part of the recording was heard. Earlier
  // fragments that were not heard are just not heard yet. Before anything was played there is nothing to
  // compare with. The mark is cleared when it expires (below).
  useEffect(() => {

    const heardSomething = rangesRef.current.length > 0;

    if (chunkDurations && heardSomething && previousTotal.current > 0 && total > previousTotal.current) {

      // Everything past the previous total is new, also when one poll added several chunks.
      setFreshFrom(previousTotal.current);

    }

    previousTotal.current = total;

  }, [chunkDurations, total]);

  // The first url goes into the active element; later ones are loaded into the other element, which takes over.
  useEffect(() => {

    swap.current += 1;

    const token = swap.current;
    const current = active();
    const spare = audioElements.current[1 - activeIndex.current];

    if (!current || !spare) return;

    if (!current.getAttribute('src')) {

      current.src = url;
      return;

    }

    const handOver = () => {

      if (token !== swap.current) return;

      // Only a playing element runs ahead while the new one loads; one that stopped at its end resumes exactly there.
      const at = current.currentTime + (current.paused ? 0 : handOverLatencySeconds);
      const takeOver = () => {

        if (token !== swap.current) return;

        // Pause or play pressed while this was loading counts: decide now.
        const resume = !current.paused || resumeOnAppend.current;

        activeIndex.current = 1 - activeIndex.current;
        resumeOnAppend.current = false;

        if (resume) {

          // The old one keeps playing until the new one does: an overlap of a few ms instead of a gap.
          const stopOld = () => {

            current.pause();
            current.removeAttribute('src');
            current.load();

          };

          spare.addEventListener('playing', stopOld, { once: true });
          spare.play().catch(() => {

            // Also when a pause aborted it. The new element holds the whole recording at the right place: it stays
            // the active one, paused, and the old one (ended, or about to be replaced) is dropped.
            spare.removeEventListener('playing', stopOld);
            stopOld();
            setPlaying(false);
            setPosition(spare.currentTime);

          });
          return;

        }

        current.removeAttribute('src');
        current.load();

      };

      if (Math.abs(spare.currentTime - at) < 0.01) {

        takeOver();
        return;

      }

      spare.addEventListener('seeked', takeOver, { once: true });
      spare.currentTime = at;

    };

    spare.addEventListener('loadedmetadata', handOver, { once: true });
    spare.src = url;

  }, [url, active]);

  // The refs are already empty when the cleanup runs: keep the elements, and drop a hand-over in flight.
  useEffect(() => {

    const elements = [...audioElements.current];

    return () => {

      swap.current += 1;
      elements.forEach((audio) => audio?.pause());

    };

  }, []);

  const seekTo = (seconds: number) => {

    const audio = active();

    if (!audio) return;

    const target = Math.min(Math.max(0, seconds), end);

    resumeOnAppend.current = false;
    audio.currentTime = target;
    lastPosition.current = target;
    setPosition(target);

  };

  // Marks what was played since the last look as heard, and stops at the limit. Called every frame and by
  // `timeupdate`: frames stop in a background tab while the audio goes on.
  const follow = useCallback((audio: HTMLAudioElement) => {

    const reached = limit && audio.currentTime >= limit;
    const now = reached ? limit : audio.currentTime;
    const step = now - lastPosition.current;

    if (step > 0 && step < maxPlayedStepSeconds) mark(lastPosition.current, now);

    lastPosition.current = now;

    if (reached) {

      audio.pause();
      audio.currentTime = limit;

    }

    return now;

  }, [limit, mark]);

  useEffect(() => {

    if (!playing) return undefined;

    let frame = 0;
    const tick = () => {

      frame = requestAnimationFrame(tick);

      const audio = active();

      if (audio) setPosition(follow(audio));

    };

    tick();

    return () => cancelAnimationFrame(frame);

  }, [playing, follow, active]);

  // Pressing play is enough for the guide to move on (a recording can be ten minutes long). Once started it
  // stays done: audio that is added later does not take it back, and heard parts are kept across reloads.
  const started = playing || ranges.length > 0;

  useEffect(() => {

    if (started) setDone(true);

  }, [started]);

  // The live badge goes away by itself 30 s after the last upload, and the new-audio mark a minute after that.
  const liveEndsAt = lastUploadAt && !final ? lastUploadAt + liveWindowMs : lastUploadAt;
  const newUntil = liveEndsAt ? liveEndsAt + newAfterLiveMs : Infinity;

  useEffect(() => {

    if (Date.now() >= newUntil) setFreshFrom(null);

  }, [expired, newUntil]);

  useEffect(() => {

    const next = [liveEndsAt, newUntil].filter((moment) => moment && moment > Date.now()).sort((a, b) => a - b)[0];

    if (!next || next === Infinity) return undefined;

    const timer = window.setTimeout(() => setExpired((count) => count + 1), next - Date.now());

    return () => window.clearTimeout(timer);

  }, [liveEndsAt, newUntil, expired]);

  const live = !!lastUploadAt && !final && Date.now() < liveEndsAt;
  const fresh = freshFrom !== null && Date.now() < newUntil ? freshFrom : null;

  const toggle = () => {

    const audio = active();

    if (!audio) return;

    if (playing) {

      resumeOnAppend.current = false;
      // Both: in the moment of a hand-over the old element is still the one that is heard.
      audioElements.current.forEach((element) => element?.pause());
      return;

    }

    // Playing again from the end of a finished recording starts it over.
    if (final && audio.currentTime >= end - 0.05) seekTo(0);

    resumeOnAppend.current = false;
    audio.play().catch(() => undefined);

  };

  // The first moment that was not played yet: the start, or the end of what was heard from the start.
  const firstUnheard = ranges.length > 0 && ranges[0][0] <= unheardStartToleranceSeconds ? ranges[0][1] : 0;

  const playFromUnheard = () => {

    seekTo(firstUnheard);
    active()?.play().catch(() => undefined);

  };

  const seekFromPointer = (event: PointerEvent<HTMLDivElement>) => {

    const { left, width } = barRef.current.getBoundingClientRect();

    seekTo(Math.min(1, Math.max(0, (event.clientX - left) / width)) * shown);

  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    // The arrow keys also scroll the page.
    event.preventDefault();
    seekTo(position + (event.key === 'ArrowLeft' ? -keyboardStepSeconds : keyboardStepSeconds));

  };

  const percent = (seconds: number) => `${shown > 0 ? Math.min(100, (seconds / shown) * 100) : 0}%`;

  // What the legend counts: heard, new (added since opening and not heard) and what is left to hear.
  // The part that is being played right now is counted up to the last whole second of the playhead (the very
  // end excepted), and everything is cut to whole seconds like the clocks, so the clock and all three counters
  // change at the same moment and always add up to the length shown.
  const atEnd = position >= end - playingTailSeconds;
  const counted = ranges.map(([from, to]): Range => (!atEnd && Math.abs(to - position) < playingTailSeconds
    ? [from, Math.max(from, Math.floor(position))]
    : [from, to]));
  const overlap = (from: number, to: number) => counted
    .reduce((sum, [start, finish]) => sum + Math.max(0, Math.min(finish, to) - Math.max(start, from)), 0);
  const wholeEnd = Math.floor(end);
  const heardTotal = Math.min(wholeEnd, Math.floor(overlap(0, end)));
  const newTotal = fresh === null ? 0 : Math.min(wholeEnd - heardTotal, Math.floor(Math.max(0, end - fresh - overlap(fresh, end))));
  const unheardTotal = Math.max(0, wholeEnd - heardTotal - newTotal);

  useEffect(() => {

    const element = barRef.current;
    const observer = new ResizeObserver(() => setBarWidth(element.clientWidth || 600));

    observer.observe(element);

    return () => observer.disconnect();

  }, []);

  // Fine buckets are merged so a bar is about `waveBarPixels` wide: little audio gets as many bars as a lot.
  const groupSize = Math.max(1, Math.round((end * waveBarPixels) / (barWidth * bucketSeconds)));
  const peaks = useMemo(() => Array.from({ length: Math.ceil(fineBars.length / groupSize) }, (_, bar) => fineBars
    .slice(bar * groupSize, (bar + 1) * groupSize)
    .reduce((max, peak) => Math.max(max, peak), 0)), [fineBars, groupSize]);

  // Not capped like `percent`: a limited (legacy) recording is clipped by the bar instead of squeezed into it.
  const waveWidth = `${shown > 0 ? (peaks.length * groupSize * bucketSeconds * 100) / shown : 0}%`;
  const wavePath = peaks.map((peak, index) => {

    const height = Math.max(0.1, Math.sqrt(peak));

    return `M${index + 0.5} ${0.5 - (height / 2)}v${height}`;

  }).join('');

  return (
    <div className={styles.box} data-guide-target="recording" data-guide-done={done ? '' : undefined}>
      <div className={styles.titleRow}>
        <Icon path={mdiHeadphones} className={styles.icon}/>
        <span className={styles.title}>{t('Alarm_Detail_DCC.Recording.Title')}</span>
      </div>
      <span className={styles.sub}>{t('Alarm_Detail_DCC.Recording.Instruction')}</span>
      {live && (
        <div className={styles.liveRow}>
          <span className={styles.live}>
            <i className={styles.dot}/>
            {t('Alarm_Detail_DCC.Recording.Live')}
          </span>
          {t('Alarm_Detail_DCC.Recording.Live_Hint')}
        </div>
      )}
      <div className={styles.player}>
        <button
          type="button"
          className={styles.play}
          onClick={toggle}
          aria-label={t(playing ? 'Alarm_Detail_DCC.Recording.Pause' : 'Alarm_Detail_DCC.Recording.Play')}
        >
          <Icon path={playing ? mdiPause : mdiPlay}/>
        </button>
        <span className={styles.time}>{formatTime(position)}</span>
        <div
          ref={barRef}
          className={styles.bar}
          role="slider"
          aria-label={t('Alarm_Detail_Chat_RecordingPosition')}
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={Math.round(end)}
          aria-valuenow={Math.round(position)}
          onKeyDown={onKeyDown}
          onPointerDown={(event) => {

            dragging.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            seekFromPointer(event);

          }}
          onPointerMove={(event) => dragging.current && seekFromPointer(event)}
          onPointerUp={() => {

            dragging.current = false;

          }}
          onPointerCancel={() => {

            dragging.current = false;

          }}
        >
          {peaks.length > 0 && (
            <div className={styles.wave}>
              <svg
                style={{ width: waveWidth }}
                viewBox={`0 0 ${peaks.length} 1`}
                preserveAspectRatio="none"
              >
                <path d={wavePath}/>
              </svg>
            </div>
          )}
          <div className={styles.rail}>
            {fresh !== null && (
              <div
                key={fresh}
                className={styles.fresh}
                style={{ left: percent(fresh), right: 0 }}
              />
            )}
            {ranges.map(([from, to]) => (
              <div key={from} className={styles.heard} style={{ left: percent(from), width: percent(to - from) }}/>
            ))}
            <div className={styles.playhead} style={{ left: percent(position) }}/>
          </div>
        </div>
        <span className={styles.time}>{formatTime(end)}</span>
      </div>
      <div className={styles.legend}>
        <span><i className={styles.legendHeard}/>{t('Alarm_Detail_DCC.Recording.Heard')} {formatTime(heardTotal)}</span>
        {fresh !== null && <span><i className={styles.legendFresh}/>{t('Alarm_Detail_DCC.Recording.New')} {formatTime(newTotal)}</span>}
        {end - firstUnheard > minUnheardSeconds ? (
          <button type="button" className={styles.unheard} onClick={playFromUnheard}>
            <Icon path={mdiPlay}/>
            {t('Alarm_Detail_DCC.Recording.Unheard')} {formatTime(unheardTotal)}
          </button>
        ) : (
          <span><i className={styles.legendUnheard}/>{t('Alarm_Detail_DCC.Recording.Unheard')} {formatTime(unheardTotal)}</span>
        )}
      </div>
      {[0, 1].map((index) => (
        <audio
          key={index}
          ref={(element) => {

            audioElements.current[index] = element;

          }}
          preload="auto"
          onPlay={(event) => isActive(event) && setPlaying(true)}
          onPause={(event) => {

            if (!isActive(event)) return;

            setPlaying(false);
            setPosition(event.currentTarget.currentTime);

          }}
          onLoadedMetadata={(event) => {

            const { duration } = event.currentTarget;

            if (isActive(event) && Number.isFinite(duration)) setMetaDuration(duration);

          }}
          onTimeUpdate={(event) => {

            if (isActive(event) && !event.currentTarget.paused) follow(event.currentTarget);

          }}
          onSeeked={(event) => isActive(event) && !playing && setPosition(event.currentTarget.currentTime)}
          onEnded={(event) => {

            if (!isActive(event)) return;

            // The last frame may not have been seen by the animation loop.
            mark(lastPosition.current, Math.min(end, event.currentTarget.duration));
            lastPosition.current = Math.min(end, event.currentTarget.duration);
            setPlaying(false);
            setPosition(lastPosition.current);
            // Also when `final` is already true: that may describe a longer file that is still loading.
            resumeOnAppend.current = true;

          }}
        />
      ))}
    </div>
  );

};
